import React, { useState } from 'react'
import { LogOut } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../hooks/use-i18n'
import roomsService from '../services/roomsService'

interface LeaveRoomModalProps {
  roomId: string
  roomName?: string
  onClose: () => void
  onSuccess?: () => void
}

const LeaveRoomModal: React.FC<LeaveRoomModalProps> = ({
  roomId,
  roomName,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await roomsService.leaveRoom(roomId)
      toast.success(t('room.leave.success'))
      onSuccess?.()
      onClose()
      navigate('/dashboard')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('room.leave.failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <LogOut className="w-5 h-5 text-destructive" />
          <h2 className="font-display text-xl font-bold text-foreground">
            {t('room.leave.title')}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          {t('room.leave.body', { room: roomName || t('room.leave.thisRoom') })}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
          >
            {t('room.leave.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading ? t('common.loading') : t('room.leave.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default LeaveRoomModal
